import { SncrErrorCode, SncrPrescriptionTypeConfig } from './types';

export const FONT_ONEST = "'Onest', 'Nunito', sans-serif";
export const FONT_INTER = "'Inter', sans-serif";

export const SNCR_COLORS = {
  primary: '#3D0E6B',
  primaryButton: '#4C1185',
  selection: '#8A6AB8',
  selectionBg: '#F5F5FF',
  badgeBg: '#CBBFE6',
  textPrimary: '#343A40',
  textBody: '#495057',
  textSecondary: '#545C62',
  textMuted: '#ADB5BD',
  balanceOk: '#1E252B',
  balanceCritical: '#BC0006',
  border: '#DEE2E6',
  cardBorder: '#E9ECEE',
  controlBg: '#F8F9FA',
  checkboxChecked: '#3C0E6B',
  checkboxBorder: '#868E96',
  checkboxIcon: '#FCFCFD',
  warningBg: '#FFAE003D',
  warningText: '#A46000',
  dangerBg: '#FDE8E8',
  dangerText: '#C92A2A',
  infoBg: '#EEF1FF',
  infoIcon: '#718AF0',
  criticalBannerBg: '#FBF0C4',
  tooltipTrigger: '#4C1D7C',
  supportDark: '#16002C',
  alertBorder: '#FFDE49',
  alertBackground: '#FFF4C0',
  overlay: 'rgba(0, 0, 0, 0.35)',
} as const;

export const SNCR_TOOLTIP_PROPS = {
  hasArrow: true,
  bg: 'primary.main',
  color: 'white',
  fontSize: '12px',
  fontWeight: 500,
  fontFamily: FONT_ONEST,
  borderRadius: '8px',
  padding: '8px',
} as const;

const NOTIFICATION_BATCH = { kind: 'adjustable', min: 10, max: 50, step: 10, default: 10 } as const;
const WHITE_PRESCRIPTION_BATCH = { kind: 'fixed', value: 1000 } as const;

export const SNCR_PRESCRIPTION_TYPES: SncrPrescriptionTypeConfig[] = [
  {
    id: 'NRA',
    name: 'Notificação de Receita A',
    tag: { bg: '#FFEFA1', color: '#694517' },
    batch: NOTIFICATION_BATCH,
    criticalBalance: 10,
  },
  {
    id: 'NRB',
    name: 'Notificação de Receita B',
    tag: { bg: '#A1CAFF', color: '#1C398E' },
    batch: NOTIFICATION_BATCH,
    criticalBalance: 10,
  },
  {
    id: 'NRB2',
    name: 'Notificação de Receita B2',
    tag: { bg: '#A1CAFF', color: '#1C398E' },
    batch: NOTIFICATION_BATCH,
    criticalBalance: 10,
  },
  {
    id: 'NRT',
    name: 'Notificação de Receita de Talidomida',
    tag: { bg: '#EEE7FF', color: '#6B409C' },
    batch: NOTIFICATION_BATCH,
    criticalBalance: 10,
  },
  {
    id: 'NRR',
    name: 'Notificação de Receita de Retinóides',
    tag: { bg: '#E6BFDF', color: '#9C4094' },
    batch: NOTIFICATION_BATCH,
    criticalBalance: 10,
  },
  {
    id: 'RCE',
    name: 'Receita de Controle Especial',
    tag: { bg: '#FFFFFF', color: '#000000', border: '#DEE2E6' },
    batch: WHITE_PRESCRIPTION_BATCH,
    criticalBalance: 200,
  },
  {
    id: 'RET',
    name: 'Receita Sujeita a Retenção',
    tag: { bg: '#FFFFFF', color: '#000000', border: '#DEE2E6' },
    batch: WHITE_PRESCRIPTION_BATCH,
    criticalBalance: 200,
  },
];

export const SESSION_POLL_INTERVAL_MS = 500;
export const AUTH_TIMEOUT_MS = 5 * 60 * 1000;

export const GOVBR_POPUP_NAME = 'sncr-govbr-login';
export const GOVBR_POPUP_FEATURES = 'width=800,height=700';

export const SNCR_CALLBACK_PATH = '/sncr/callback.br';

export const SNCR_TEXTS = {
  anvisaCfoError: 'Conexão Anvisa-CFO instável. Tente mais tarde',
  unknownInternalServerError: 'Erro ao realizar a solicitação. Tente mais tarde',
  panelTitle: 'Gestão de numeração SNCR',
  panelSubtitle: 'Consulte e gerencie abaixo seu estoque de numerações na Mevo',
  criticalPreselectedBanner: 'Estoques críticos vêm pré-selecionados',
  criticalPreselectedAction: 'Entendi',
  anvisaDefinitionTitle: 'Definição Anvisa - SNCR',
  anvisaDefinitionItems: [
    'Notificação de receita: no mínimo 10 e no máximo 50 numerações diárias',
    'Receitas brancas: 1000 numerações a cada requisição, sendo no máximo 3 requisições por mês',
  ],
  fixedBatchTooltip: 'Limite definido pela Anvisa, não é possível ter controle sobre o lote',
  govBrRedirectNotice: 'Você será temporariamente redirecionado(a) ao Gov.br',
  requestQuantityLabel: 'Solicitar:',
  fixedBatchLabel: 'Lote fixo:',
  back: 'Voltar',
  requestNumbering: 'Solicitar numeração',
  cancel: 'Cancelar',
  authenticateViaGovBr: 'Autenticar via Gov.br',
  authRequiredTitle: 'Autenticação obrigatória',
  authRequiredDescription:
    'Você será redirecionado(a) para o ambiente seguro do Governo Federal. Após a validação e solicitação, retornaremos automaticamente.',
  waitingGovBr: 'Aguardando Gov.br…',
  processingTitle: 'Recarregando estoques',
  processingSubtitle: 'Aguarde enquanto solicitamos as numerações',
  processing: 'Processando',
  successTitle: 'Numerações obtidas com sucesso!',
  successSubtitle: 'Seu estoque foi completamente recarregado',
  partialTitle: 'Recarregamento parcial de numerações',
  partialSubtitle: 'Apenas o saldo disponível no momento foi liberado',
  failureTitle: 'Não foi possível recarregar',
  failureTitleMonthly: 'Não foi possível recarregar totalmente',
  failureSubtitleDaily: 'Seu estoque atingiu o limite diário',
  failureSubtitleMonthly: 'Seu estoque atingiu o limite mensal',
  failureSubtitleGeneric: 'Nenhum saldo foi liberado no momento',
  gotIt: 'Entendi',
  quotaRequestFailed: 'Não foi possível emitir esta cota',
  itemDailyLimitReached: 'Limite diário atingido: {limitReached}',
  itemMonthlyLimitReached: 'Limite mensal atingido: 3000',
  itemPartialDailyLimit: 'Quantidade solicitada superior ao saldo diário disponível: {balance}',
  itemNoQuotasAvailable: 'Não há numerações suficientes no SNCR',
  itemNoQuotaAvailableWithPrescriber:
    '{prescriberInfo} sem saldo disponível no SNCR. Cheque com sua vigilância local',
  itemNoQuotaAvailableGeneric: 'Sem saldo disponível no SNCR. Cheque com sua vigilância local',
  itemWrongProfessionalNumberWithPrescriber:
    '{prescriberInfo} não está disponível ou ativo no Conselho. Cheque seus dados',
  itemWrongProfessionalNumberGeneric: 'Prescritor não está disponível ou ativo no Conselho. Cheque seus dados',
  itemWrongPrescriber: '{prescriberInfo} não está vinculado ao CPF autenticado via Gov.br. Cheque seus dados',
  itemAnvisaCpfError: 'CPF autenticado no Gov.br sem vínculo no {council}. Cheque seus dados',
  itemUnknownError: 'Sistema da Anvisa instável. Tente mais tarde {status}',
};

interface SncrErrorContent {
  title: string;
  description: string;
  actionLabel: string;
  action: 'retry' | 'dismiss';
}

export const SNCR_ERROR_CONTENT: Record<SncrErrorCode, SncrErrorContent> = {
  professional_data_missing: {
    title: 'Não foi possível solicitar as numerações',
    description:
      'Os dados profissionais do prescritor estão incompletos ou inválidos. Verifique o registro, o órgão emissor e a UF antes de tentar novamente.',
    actionLabel: 'Entendi',
    action: 'dismiss',
  },
  popup_blocked: {
    title: 'A janela do Gov.br não abriu',
    description:
      'Seu navegador ou o sistema em que a plataforma está integrada bloqueou a janela do Gov.br. ' +
      'Habilite pop-ups para este site e tente novamente.',
    actionLabel: 'Tentar novamente',
    action: 'retry',
  },
  login_abandoned: {
    title: 'Autenticação não concluída',
    description:
      'A janela do Gov.br foi fechada antes da conclusão do login. Para solicitar as numerações é ' +
      'necessário concluir a autenticação.',
    actionLabel: 'Tentar novamente',
    action: 'retry',
  },
  auth_expired: {
    title: 'Sessão expirada',
    description: 'A autenticação Gov.br expirou. Autentique-se novamente para continuar a solicitação.',
    actionLabel: 'Tentar novamente',
    action: 'retry',
  },
  service_unavailable: {
    title: 'Não foi possível recarregar seus estoques',
    description: 'Não conseguimos nos comunicar com o serviço da ANVISA agora. Tente novamente em alguns minutos.',
    actionLabel: 'Atualizar estoques',
    action: 'retry',
  },
  unknown_error: {
    title: 'Não foi possível concluir a solicitação.',
    description: 'Erro ao se conectar com o sistema da ANVISA. Tente novamente',
    actionLabel: 'Entendi',
    action: 'dismiss',
  },
};
